// REST-Client für die Changelog-Popup-v2-Routen des maco-runner.
// Spiegelt routes_changelog.py + /api/llm/draft-changelog-ticket.

import type { ChangelogEntry } from "@/types/changelog";

export class ChangelogApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`Changelog-API (${status}): ${detail}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { getToken } = await import("./auth");
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail ?? detail;
    } catch { /* nicht JSON */ }
    throw new ChangelogApiError(res.status, detail);
  }
  return res.json() as Promise<T>;
}

export interface DocIndex { doctypes: Record<string, { formatversions: string[] }> }
export interface ChangelogComment {
  id: number; run_id: string; entry_key: string; author: string; text: string; created_at: string;
}
export interface TicketDraft { title: string; body_markdown: string; warnings: string[] }
export interface JiraCreated { jira_key: string; jira_url: string }

const key = (s: string) => encodeURIComponent(s);

export const changelogApi = {
  getDocIndex: () => request<DocIndex>("/api/changelog/doc-index"),
  getMigDoc: (fv: string, fmt: string) =>
    request<Record<string, unknown>>(`/api/changelog/doc/mig/${key(fv)}/${key(fmt)}`),
  getAhbDoc: (fv: string, fmt: string, pruefid: string) =>
    request<Record<string, unknown>>(`/api/changelog/doc/ahb/${key(fv)}/${key(fmt)}/${key(pruefid)}`),
  listComments: (runId: string, entryKey: string) =>
    request<{ comments: ChangelogComment[] }>(`/api/changelog/comments/${key(runId)}/${key(entryKey)}`),
  addComment: (runId: string, entryKey: string, body: { author: string; text: string }) =>
    request<ChangelogComment>(`/api/changelog/comments/${key(runId)}/${key(entryKey)}`, {
      method: "POST", body: JSON.stringify(body),
    }),
  deleteComment: (runId: string, entryKey: string, id: number) =>
    request<{ deleted: number }>(`/api/changelog/comments/${key(runId)}/${key(entryKey)}/${id}`, {
      method: "DELETE",
    }),
  draftTicket: (entry: ChangelogEntry) =>
    request<TicketDraft>("/api/llm/draft-changelog-ticket", {
      method: "POST",
      body: JSON.stringify({
        aend_id: entry.aend_id, doc_diff_id: entry.doc_diff_id, pruefid: entry.pruefid,
        doctype: entry.doctype, format: entry.format, formatversion: entry.formatversion,
        ort: entry.ort, before: entry.before, after: entry.after, reason: entry.reason,
        change_type: entry.change_type, provenance: entry.provenance,
      }),
    }),
  createJira: (runId: string, entryKey: string, body: { title: string; body_markdown: string }) =>
    request<JiraCreated>("/api/changelog/jira", {
      method: "POST",
      body: JSON.stringify({ run_id: runId, entry_key: entryKey, ...body }),
    }),
};
